import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  CurrencyRupee,
  Groups,
  EventSeat,
  BookOnline,
  AccessTime,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../../theme/tokens';
import { MOCK_STATS, MOCK_SESSIONS, MOCK_LIBRARY } from '../crmMockData';

const MotionCard = motion(Card);

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const statCards = [
  {
    label: 'Revenue Today',
    value: `₹${MOCK_STATS.revenueToday.toLocaleString('en-IN')}`,
    icon: <CurrencyRupee />,
    color: '#16A34A',
  },
  {
    label: 'Active Sessions',
    value: `${MOCK_STATS.activeSessions}`,
    icon: <Groups />,
    color: '#0EA5E9',
  },
  {
    label: 'Occupancy',
    value: `${MOCK_STATS.occupancyPct}%`,
    icon: <EventSeat />,
    color: '#F26522',
  },
  {
    label: 'New Bookings',
    value: `${MOCK_STATS.newBookingsToday}`,
    icon: <BookOnline />,
    color: '#F7B32B',
  },
];

function DemoHomeTab() {
  return (
    <Box
      component={motion.div}
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
    >
      {/* Library header */}
      <MotionCard
        variants={itemVariants}
        sx={{
          borderRadius: RADIUS.xl,
          mb: 2,
          background: GRADIENTS.brand,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'white' }}>
            {MOCK_LIBRARY.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {MOCK_LIBRARY.occupied} / {MOCK_LIBRARY.capacity} seats occupied
          </Typography>
        </CardContent>
      </MotionCard>

      {/* Stat cards — 2×2 grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          mb: 2,
        }}
      >
        {statCards.map((s) => (
          <MotionCard
            key={s.label}
            variants={itemVariants}
            sx={{ borderRadius: RADIUS.xl }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: `${s.color}18`,
                  color: s.color,
                  mb: 1,
                }}
              >
                {s.icon}
              </Avatar>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {s.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
            </CardContent>
          </MotionCard>
        ))}
      </Box>

      {/* Active sessions preview */}
      <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Groups color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Active Sessions
            </Typography>
          </Box>
          <List disablePadding>
            {MOCK_SESSIONS.slice(0, 3).map((s) => (
              <ListItem
                key={s.id}
                divider
                sx={{ px: 2, py: 1.25 }}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: GRADIENTS.brand,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}
                  >
                    {s.studentName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {s.studentName}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <AccessTime sx={{ fontSize: 11, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {s.checkInTime}
                      </Typography>
                    </Box>
                  }
                />
                <Chip
                  label={`Seat ${s.seatNo}`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    height: 20,
                    bgcolor: 'action.selected',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </MotionCard>
    </Box>
  );
}

export default DemoHomeTab;
